/** What a page element must offer for {@link CursorStyle} to drive it. Only `cursor` is ever touched. */
export interface CursorTarget {
  /** The element's inline style. */
  readonly style: { cursor: string };
}

const HIDDEN_CURSOR = 'none';
const INHERITED_CURSOR = '';

/**
 * Shows and hides the operating system's cursor over the canvas.
 *
 * A game that draws its own cursor as an entity needs the real arrow out of the way, and that is a CSS
 * property on the canvas rather than anything the renderer can reach. This is the whole of the service:
 * there is no custom cursor image here, because a sprite entity is the better renderer for one. A CSS
 * cursor image is capped near 32 pixels, cannot animate, cannot tint and cannot sort against the scene.
 *
 * Hiding is the game's call rather than something `TrackPointer` does on attach: two cursor entities in
 * one scene would otherwise fight over a single CSS property with nothing counting who still wants it
 * hidden.
 *
 * A context built without a target, as every test harness is, gets an instance whose calls do nothing.
 * That is what keeps the engine's own suite free of a DOM.
 */
export class CursorStyle {
  readonly #target: CursorTarget | undefined;

  #hidden = false;

  constructor(target?: CursorTarget) {
    this.#target = target;
  }

  /** Whether {@link CursorStyle.hide} was called and not yet undone by {@link CursorStyle.reset}. */
  get isHidden(): boolean {
    return this.#hidden;
  }

  /** Hides the operating system cursor over the canvas. */
  hide(): void {
    this.#apply(HIDDEN_CURSOR, true);
  }

  /** Stops overriding the cursor, handing the decision back to the page's own stylesheet. */
  reset(): void {
    this.#apply(INHERITED_CURSOR, false);
  }

  #apply(cursor: string, hidden: boolean): void {
    this.#hidden = hidden;
    if (this.#target !== undefined) {
      this.#target.style.cursor = cursor;
    }
  }
}
